import { CurrencyPipe } from '@angular/common';
import { Component, inject, Inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import {
    FormBuilder,
    FormGroup,
    AbstractControl,
    ValidationErrors,
    ValidatorFn,
    Validators
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { finalize, Subscription } from 'rxjs';
import { Wallet } from 'src/app/model/Wallet';
import { WalletService } from 'src/app/services/wallet/wallet.service';

/** Data provided to the autopay confirmation dialog */
export interface AutopayConfirmationDialogData {
    amount: number;
    date: Date;
    dueby: Date;
}

/** Response data from autopay confirmation dialog */
export interface AutopayConfirmationDialogResponse {
    paymentamount: string;
    date: string;
    walletId: string;
}

/**
 * Dialog component for confirming autopay setup
 * Allows user to confirm payment amount, select date, and authorize payment
 */
@Component({
    selector: 'app-autopay-confirmation-dialog',
    templateUrl: './autopay-confirmation-dialog.component.html',
    styleUrls: ['./autopay-confirmation-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AutopayConfirmationDialogComponent implements OnInit, OnDestroy {
    /** Dependency injections */
    private _fb: FormBuilder = inject(FormBuilder);
    private _dialogRef: MatDialogRef<AutopayConfirmationDialogComponent> = inject(MatDialogRef);
    private _walletService: WalletService = inject(WalletService);
    private _currencyPipe: CurrencyPipe = inject(CurrencyPipe);

    /** Dialog data */
    public data: AutopayConfirmationDialogData = inject(MAT_DIALOG_DATA);

    /** Reactive form for autopay confirmation */
    form: FormGroup;

    /** Date validation */
    maxDate: Date;
    today = new Date();

    /** Available wallets for payment */
    private _walletSub: Subscription | undefined;
    wallets: Array<Wallet> = [];
    
    /** Currently selected wallet nickname */
    selectedNickname = '';

    /** Loading states */
    loadingWallet = true;

    /**
     * Constructor to initialize form constants
     */
    constructor() {
        this.maxDate = this.data.dueby;

        // Create form
        this.form = this._fb.group({
            paymentamount: [this._currencyPipe.transform(this.data.amount, "USD", 'symbol', '1.2-2'), [Validators.required]],
            date: [null, [Validators.required, this.maxDateValidator(this.maxDate)]],
            walletId: [null, [Validators.required]]
        });
    }

    /**
     * On initialization, load wallets
     */
    ngOnInit() {
        // Load wallets
        this.loadingWallet = true;

        // Subscribe to wallets observable
        this._walletSub = this._walletService.wallets$.pipe(
            finalize(() => this.loadingWallet = false),
        ).subscribe({
            next: (wallets: Wallet[]) => {
                this.wallets = wallets;
                this.setActiveWallet();
            }
        });

        this.wallets = this._walletService.getWallets() || [];
        if (this.wallets.length > 0) {
            this.loadingWallet = false;
        } else {
            this.setActiveWallet();
        }

    }

    /**
     * On destroy, unsubscribe from wallets observable
     */
    ngOnDestroy() {
        console.log("destroy")
        if (this._walletSub) this._walletSub.unsubscribe();
    }

    /**
     * Custom validator to ensure date doesn't exceed maximum allowed date
     */
    private maxDateValidator(max: Date): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const entered = new Date(control.value);
            return entered > max
                ? { maxDate: { required: max, actual: control.value } }
                : null;
        };
    }

    /**
     * Helper method to set the active wallet
     */
    private setActiveWallet(): void {
        // Find active wallet and set form default
        const activeWallet = this.wallets.find(w => w.active === '1');
        if (activeWallet == undefined) {
            // No active walle found
            this.selectedNickname = '';
            this.form.get('walletId')!.setValue(null);
        } else {
            this.selectedNickname = activeWallet.nickname;
            this.form.get('walletId')!.setValue(activeWallet.achwalletID);
        }
    }

    /**
     * Cancel dialog and close without returning data
     */
    onCancel(): void {
        this._dialogRef.close();
    }

    /**
     * Confirm autopay setup and close with form data
     */
    onConfirm(): void {
        if (this.form.valid) {
            const result: AutopayConfirmationDialogResponse = this.form.value;
            this._dialogRef.close(result);
        }
    }
}
